import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  LinearProgress,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import type { UploadQueueItem } from "../../types/upload-queue.types";
import { formatFileSize } from "../../utils/formatFileSize";
import { getFileExtensionIcon } from "../../utils/file-type-icon";

type Props = {
  items: UploadQueueItem[];
  replacingItemId?: string | null;
  retryingItemId?: string | null;
  onReplace?: (item: UploadQueueItem) => void;
  onRetry?: (item: UploadQueueItem) => void;
  canRetryItem?: (item: UploadQueueItem) => boolean;
  onDismissFinished?: () => void;
};

function StatusIcon({ status }: { status: UploadQueueItem["status"] }) {
  switch (status) {
    case "uploaded":
      return <CheckCircleOutlineIcon sx={{ fontSize: 20, color: "#16A34A" }} />;
    case "failed":
      return <ErrorOutlineIcon sx={{ fontSize: 20, color: "#DC2626" }} />;
    case "name_conflict":
      return <WarningAmberOutlinedIcon sx={{ fontSize: 20, color: "#D97706" }} />;
    case "batching":
    case "uploading":
    case "completing":
      return <CircularProgress size={18} />;
    default:
      return <HourglassEmptyIcon sx={{ fontSize: 20, color: "#9CA3AF" }} />;
  }
}

function statusLabel(status: UploadQueueItem["status"]): string {
  switch (status) {
    case "pending":
      return "Aguardando";
    case "batching":
      return "Preparando envio";
    case "uploading":
      return "Enviando";
    case "completing":
      return "Finalizando";
    case "uploaded":
      return "Enviado";
    case "failed":
      return "Falhou";
    case "name_conflict":
      return "Nome duplicado";
    default:
      return status;
  }
}

export function UploadProgressList({
  items,
  replacingItemId = null,
  retryingItemId = null,
  onReplace,
  onRetry,
  canRetryItem,
  onDismissFinished,
}: Props) {
  if (items.length === 0) return null;

  const hasFinished = items.some(
    (item) =>
      item.status === "uploaded" ||
      item.status === "failed" ||
      item.status === "name_conflict"
  );

  const inProgress =
    replacingItemId !== null ||
    retryingItemId !== null ||
    items.some(
      (item) =>
        item.status === "batching" ||
        item.status === "uploading" ||
        item.status === "completing"
    );

  return (
    <Box
      mb={2}
      border="1px solid #E5E7EB"
      borderRadius={1}
      bgcolor="#FFFFFF"
      overflow="hidden"
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        px={2}
        py={1.5}
        borderBottom="1px solid #E5E7EB"
        bgcolor="#F9FAFB"
      >
        <Typography fontSize={14} fontWeight={600} color="#374151">
          Envio de arquivos
        </Typography>
        {hasFinished && !inProgress && onDismissFinished && (
          <IconButton size="small" onClick={onDismissFinished} aria-label="Limpar lista">
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {inProgress && <LinearProgress />}

      <Box display="flex" flexDirection="column">
        {items.map((item) => {
          const Icon = getFileExtensionIcon(
            item.displayName.includes(".")
              ? item.displayName.split(".").pop() ?? ""
              : ""
          );

          return (
            <Box
              key={item.id}
              display="flex"
              alignItems="flex-start"
              gap={1.5}
              px={2}
              py={1.5}
              borderBottom="1px solid #F3F4F6"
              sx={{ "&:last-child": { borderBottom: 0 } }}
            >
              <Icon sx={{ fontSize: 20, color: "#1361A4", mt: 0.25 }} />
              <Box flex={1} minWidth={0}>
                <Typography fontSize={14} color="#1F2937" noWrap>
                  {item.displayName}
                </Typography>
                <Typography fontSize={12} color="#6B7280">
                  {formatFileSize(item.sizeBytes)} — {statusLabel(item.status)}
                </Typography>
                {item.warning && item.status === "pending" && (
                  <Typography fontSize={12} color="#D97706" mt={0.5}>
                    {item.warning}
                  </Typography>
                )}
                {item.errorMessage && (
                  <Typography fontSize={12} color="#DC2626" mt={0.5}>
                    {item.errorMessage}
                  </Typography>
                )}
                {item.status === "name_conflict" && onReplace && (
                  <Box mt={1}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => onReplace(item)}
                      disabled={replacingItemId !== null || retryingItemId !== null}
                      sx={{
                        textTransform: "none",
                        fontSize: 12,
                        borderColor: "#D97706",
                        color: "#D97706",
                        "&:hover": {
                          borderColor: "#B45309",
                          bgcolor: "#FFFBEB",
                        },
                      }}
                    >
                      {replacingItemId === item.id ? "Substituindo..." : "Substituir"}
                    </Button>
                  </Box>
                )}
                {item.status === "failed" &&
                  onRetry &&
                  (canRetryItem?.(item) ?? false) && (
                    <Box mt={1}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => onRetry(item)}
                        disabled={replacingItemId !== null || retryingItemId !== null}
                        sx={{ textTransform: "none", fontSize: 12 }}
                      >
                        {retryingItemId === item.id ? "Tentando novamente..." : "Tentar novamente"}
                      </Button>
                    </Box>
                  )}
              </Box>
              <StatusIcon status={item.status} />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
